import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { DoraButton, DoraSelectMultiple, DoraSelect, DoraDatePicker } from "dora-shared/frontend";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, RootState } from "../../../../redux-store";
import { useSelector } from "react-redux";
import { loadDrivers } from "../../../../ducks/data/drivers";
import { selectDrivers } from "../../../../ducks/data/drivers/selectors";
import {
  startCreateCargoOnDriverRoutes,
  fetchCopyCargoToDriverRoutesJob,
  resetAfterCreateOnDriverRoutes,
} from "../../../../ducks/app/cargo-dialog";
import { CargoData } from "../../../../ducks/app/cargo-dialog/types";
import { useConfirmDropDeletedExtraCo2Types } from "../../ConfirmDropDeletedExtraCo2Types";
import {
  draftLinesCleared,
  draftLinesQuantitiesReset,
} from "../../../../ducks/data/draft-invoice-lines";
import { clearSelectedTemplate } from "../../../../ducks/data/client-order-templates";
import { notifyL, notify } from "../../../../ducks/notifications";
import { DateTime } from "luxon";
import { selectDefaultCreateCopyOption } from "../../../../ducks/data/user-settings/selectors";
import { DefaultCopyOption } from "dora-contracts";

type InvoiceMode = "NONE" | "WITH" | "WITH_RESET";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 180_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// The selector defaults to the invoice handling implied by the user's default
// create-copy option (reset quantities when the default is itself "copy to
// drivers' routes").
const invoiceModeFromCopyOption = (option: DefaultCopyOption): InvoiceMode => {
  switch (option) {
    case "COPY_ORDER":
      return "NONE";
    case "COPY_WITH_INVOICE_LINES":
      return "WITH";
    default:
      return "WITH_RESET";
  }
};

// The new-cargo equivalent of CopyCargoToDriverRoutesDialog: instead of
// copying an already-persisted cargo, it creates the submitted (not yet
// saved) cargo directly on each selected driver's route in one request, then
// resets the create modal the same way "create and make copy" does, ready to
// repeat the action.
const CreateCargoOnDriverRoutesDialog = ({
  onClose,
  cargoFormData,
}: {
  onClose: () => void;
  cargoFormData: CargoData;
}) => {
  const { t } = useTranslation(["components", "shipmentModal"]);
  const dispatch = useAppDispatch();
  const {
    ask: askToDropDeletedExtraCo2Types,
    dialog: dropDeletedExtraCo2TypesDialog,
  } = useConfirmDropDeletedExtraCo2Types("SAVE");
  const drivers = useSelector(selectDrivers);
  const defaultCreateCopyOption = useSelector(selectDefaultCreateCopyOption);
  const draftLines = useSelector(
    (state: RootState) => state.data.draftInvoiceLines.lines
  );
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  // Defaults to today — the day the user chooses to create on.
  const [date, setDate] = useState<DateTime>(DateTime.now());
  const [invoiceMode, setInvoiceMode] = useState<InvoiceMode>(() =>
    invoiceModeFromCopyOption(defaultCreateCopyOption)
  );

  useEffect(() => {
    dispatch(loadDrivers());
  }, [dispatch]);

  const options = useMemo(
    () =>
      drivers
        .filter((d) => d.active)
        .map((d) => ({
          id: d.id,
          label: `${d.number} - ${d.firstName} ${d.lastName}`,
        })),
    [drivers]
  );

  const selectedValues = useMemo(
    () => options.filter((o) => selectedDriverIds.includes(o.id)),
    [options, selectedDriverIds]
  );

  const submitDisabled =
    requestInProgress || selectedDriverIds.length === 0 || !date?.isValid;

  const invoiceModeOptions = useMemo(
    () => [
      {
        value: "NONE",
        label: t("shipmentModal:createAndCopyToDriverRoutes.invoiceModeNone"),
      },
      {
        value: "WITH",
        label: t("shipmentModal:createAndCopyToDriverRoutes.invoiceModeWith"),
      },
      {
        value: "WITH_RESET",
        label: t(
          "shipmentModal:createAndCopyToDriverRoutes.invoiceModeWithReset"
        ),
      },
    ],
    [t]
  );

  const onCreateClick = async () => {
    if (selectedDriverIds.length === 0 || !date?.isValid) {
      return;
    }
    setRequestInProgress(true);
    setProgress({ done: 0, total: selectedDriverIds.length });
    try {
      const invoiceLines =
        invoiceMode === "NONE"
          ? []
          : draftLines.map((l) => ({
              productId: l.productId,
              unitId: l.unitId,
              description: l.description,
              quantity: invoiceMode === "WITH_RESET" ? "1" : l.quantity,
              amount: l.amount,
              discountPercentage: l.discountPercentage,
              percentage: l.percentage,
              isFromRoadToll: l.isFromRoadToll,
            }));

      const start = (dropDeletedExtraCo2Types: boolean) =>
        dispatch(
          startCreateCargoOnDriverRoutes({
            formData: cargoFormData,
            driverIds: selectedDriverIds,
            date: date.toISODate(),
            invoiceLines,
            dropDeletedExtraCo2Types,
          })
        ).unwrap();
      let started = await start(false);
      if ("refused" in started) {
        if (!(await askToDropDeletedExtraCo2Types())) {
          return;
        }
        started = await start(true);
        if ("refused" in started) {
          return;
        }
      }
      const { jobId } = started;

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      // Poll the job until it completes, fails, or times out.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const job = await dispatch(
          fetchCopyCargoToDriverRoutesJob(jobId)
        ).unwrap();
        setProgress({ done: job.results.length, total: job.total });
        if (job.status === "COMPLETED") {
          dispatch(
            notifyL({
              namespace: "shipmentModal",
              key: "createAndCopyToDriverRoutes.success",
              type: "success",
            })
          );
          if (invoiceMode === "NONE") {
            dispatch(draftLinesCleared());
          } else if (invoiceMode === "WITH_RESET") {
            dispatch(draftLinesQuantitiesReset());
          }
          dispatch(clearSelectedTemplate());
          dispatch(resetAfterCreateOnDriverRoutes(cargoFormData));
          onClose();
          return;
        }
        if (job.status === "FAILED") {
          dispatch(
            notify({
              message: job.error ?? "Create failed",
              type: "error",
            })
          );
          return;
        }
        if (Date.now() > deadline) {
          dispatch(
            notifyL({
              namespace: "shipmentModal",
              key: "createAndCopyToDriverRoutes.timeout",
              type: "warning",
            })
          );
          return;
        }
        await sleep(POLL_INTERVAL_MS);
      }
    } catch {
      // The startCreateCargoOnDriverRoutes / fetchCopyCargoToDriverRoutesJob thunks have already
      // reported it and shown the error toast.
    } finally {
      setRequestInProgress(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm">
      {dropDeletedExtraCo2TypesDialog}
      <DialogTitle>
        {t("shipmentModal:createAndCopyToDriverRoutes.title")}
      </DialogTitle>
      {requestInProgress ? (
        <DialogContent>
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <CircularProgress size={32} />
            <span>
              {t("shipmentModal:createAndCopyToDriverRoutes.progress", {
                done: progress?.done ?? 0,
                total: progress?.total ?? selectedDriverIds.length,
              })}
            </span>
          </div>
        </DialogContent>
      ) : (
        <DialogContent>
          <div>{t("shipmentModal:createAndCopyToDriverRoutes.text")}</div>
          <div className="mt-4">
            <div>
              {t("shipmentModal:createAndCopyToDriverRoutes.dateLabel")}
            </div>
            <div className="mt-2">
              <DoraDatePicker
                id="create-on-driver-routes-date"
                value={date}
                onChange={(d) => d && setDate(d)}
              />
            </div>
          </div>
          <div className="mt-4">
            <div>
              {t("shipmentModal:createAndCopyToDriverRoutes.driversLabel")}
            </div>
            <div className="mt-2">
              <DoraSelectMultiple
                className="w-full"
                options={options}
                selectedValues={selectedValues}
                onChange={(ids: string[]) => setSelectedDriverIds(ids)}
                placeholder={t(
                  "shipmentModal:createAndCopyToDriverRoutes.driversPlaceholder"
                )}
              />
            </div>
          </div>
          <div className="mt-4">
            <div>
              {t(
                "shipmentModal:createAndCopyToDriverRoutes.invoiceModeLabel"
              )}
            </div>
            <div className="mt-2">
              <DoraSelect
                className="w-full"
                fullWidth
                options={invoiceModeOptions}
                value={invoiceMode}
                onChange={(v) => v && setInvoiceMode(v as InvoiceMode)}
              />
            </div>
          </div>
        </DialogContent>
      )}
      <DialogActions>
        <div className="flex gap-2 items-center">
          <DoraButton
            variant="primary"
            onClick={onClose}
            disabled={requestInProgress}
          >
            {t("components:buttonLabels.cancel")}
          </DoraButton>
          <DoraButton
            variant="primary-filled"
            onClick={onCreateClick}
            disabled={submitDisabled}
          >
            {t("shipmentModal:createAndCopyToDriverRoutes.submit")}
          </DoraButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCargoOnDriverRoutesDialog;
